import { fetchEntries, fetchOneEntry } from '@builder.io/sdk-vue';
import {generateKeyFromObject} from "~/utils/string";

export const getBuilderList = async ({
    model = 'pages',
    query = {},
    sort = {},
    locale = 'nl-NL',
    limit = 10,
    offset = 0,
    apiKey = null
},  nuxtApp: any = null, redisCacheBurst = false) => {
    const options = {
        model: model,
        apiKey: apiKey,
        locale: locale,
        query: query,
        sort: sort,
        limit: limit,
        offset: offset
    }

    if(import.meta.server){

        let params = {
            options,
            key : options.model+'-'+await generateKeyFromObject(options),
            redisCacheBurst
        }

        return useAjaxServer("/api/builder/fetch", {
            method: "post",
            params,
        })
    }

    try {
        return fetchEntries(options);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp(); 
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - fetchEntries',
            options
        )
        throw error;
    }
};

 
export const getPageContent = async (
    urlPath = '/404',
    locale = 'nl-NL',
    apiKey: any = null,
    nuxtApp: any = null
) => {
    try {
        const options = {
            locale: locale === 'nl-NL'?'Default':locale,
            model: 'page',
            apiKey: apiKey,
            query: {},
            userAttributes: {
                urlPath: urlPath
            },
            options: {
                fields: "data"
            },
        }

        if(import.meta.server){

            let params = {
                method:'fetchOneEntry',
                options,
                key : 'page'+'-'+urlPath+'-'+await generateKeyFromObject(options)
            }

            return useAjaxServer("/api/builder/fetch", {
                method: "post",
                params,
            })
        }

        return fetchOneEntry(options);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - fetchOneEntry',
            options
        );
        throw error;
    }
};

export const getPopularKeywords = async (apiKey: any,locale:any) => {

    const options = {
        model: 'popular-keywords',
        query: {},
        sort: {},
        locale,
        limit: 10,
        offset: 0,
        apiKey: apiKey,
    }

    try {
        const results = await getBuilderList(options);

        if (results?.length < 1) {
            return []
        }

        return results?.map(item => ({ keyword: item.data?.keyword || '' }));

    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - getPopularKeywords',
            options
        );
        throw error;
    }
};


export const getHeaderMenu = async (locale: any, apiKey: any,
                                    nuxtApp: any = null,
                                    redisCacheBurst = false) => {
    const options = {
        model: 'header-links',
        locale: locale,
        apiKey: apiKey,
    }
    try {
        return await getBuilderList(options,nuxtApp,redisCacheBurst);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
            ...options,
            builderError: true
        }
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - getHeaderMenu',
            errorMeta
        );
        throw error;
    }
};

export const getBlogList = async (apiKey: any, opts: object,
                                  nuxtApp: any = null) => {
    const options = {
        model: 'blogs',
        query: {},
        sort: {},
        limit: 100,
        offset: 0,
        apiKey,
        ...opts
    }

    try {
        return getBuilderList(options);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - getBlogList',
            options
        );
        throw error;
    }
};

export const getFooterLinks = async (apiKey: any, opts: object,
                                     nuxtApp: any = null,redisCacheBurst = false) => {
    const options = {
        model: 'footer-links',
        apiKey: apiKey,
        ...opts
    }
    try {
        return getBuilderList(options,nuxtApp,redisCacheBurst);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
            ...options,
            builderError: true
        }
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - getFooterLinks',
            errorMeta
        );
        throw error;
    }
};

export const getProCardPopUpConsentData = async (apiKey: any, opts: object,
                                                 nuxtApp: any = null) => {
    const options = {
        model: 'pro-card-terms-and-condition',
        apiKey: apiKey,
        ...opts
    }

    try {
        return getBuilderList(options);
    } catch (error) {
        const { $customClientErrorHandler } = useNuxtApp();
        let errorMeta = {
            ...options,
            builderError: true
        }
        $customClientErrorHandler(
            error,
            'critical',
            'Builder Error - getProCardPopUpConsentData',
            errorMeta
        );
        throw error;
    }
}; 

export const getList = async (
  apiKey: any,
  model = "pages",
  query = {},
  sort = {},
  limit = 10,
  offset = 0,
  nuxtApp: any = null,
  redisCacheBurst = false
) => {
  let locale = "nl-NL";
  if (nuxtApp && nuxtApp.$i18n) {
    const i18n = { global: nuxtApp.$i18n };
    locale: i18n.global.locale.value;
  }

  let options = {
    locale,
    model,
    apiKey,
    query,
    sort,
    limit,
    offset,
    userAttributes: { locale: undefined },
  };

  if (import.meta.server) {
    let params = {
      options,
      key: model + "-" + (await generateKeyFromObject(options)),
      redisCacheBurst,
    };

    return useAjaxServer("/api/builder/fetch", {
      method: "post",
      params,
    });
  }

  try {
    return fetchEntries(options);
  } catch (error) {
    let errorMeta = {
      model,
      query,
      builderServiceError: true,
    };
    const { $customClientErrorHandler } = useNuxtApp();
    $customClientErrorHandler(
      error,
      "high",
      "Builder Service Error - trying to fetch builder list using getList()",
      errorMeta
    );
    throw error;
  }
};

export const getItem = async (
  apiKey: any,
  model = "pages",
  userAttributes = {},
  options = {},
  query = {},
  nuxtApp: any = null
) => {
  let locale = nuxtApp.$i18n.defaultLocale;
  const builderOptions = {
    locale,
    model,
    apiKey,
    query,
    userAttributes,
    options,
    cachebust: true,
    cacheSeconds: 0,
  };

  try {
    return await fetchOneEntry(builderOptions);
  } catch (error) {
    const { $customClientErrorHandler } = useNuxtApp();
    let errorMeta = {
      model,
      query,
      userAttributes,
      builderServiceError: true,
    };

    $customClientErrorHandler(
      error,
      "high",
      "Builder Service Error - trying to fetch items using getItem()",
      errorMeta
    );
    throw error;
  }
};

export const getContentModel = async (model: string, apiKey: string) => {
  try {
    const url =
      useRuntimeConfig().public.builderBaseUrl + model + `?apiKey=${apiKey}`;
    return await useAjax(url, {
      headers: {
        accept: "*/*",
      },
      show_notifications: false,
      show_loading_indicator: false,
    });
  } catch (error) {
    const { $customClientErrorHandler } = useNuxtApp();
    let errorMeta = {
      model,
      apiKey,
      builderServiceError: true,
    };

    $customClientErrorHandler(
      error,
      "high",
      "Builder Service Error - trying to fetch content model using getContentModel()",
      errorMeta
    );
    throw error;
  }
};